import { z } from "zod";

import * as DbEnum from "../../generated/db-enum";
import { enumReadSchema } from "../../shared/core/enum-read.schema";

/**
 * Where an app's stored icon came from.
 *
 * - `UPLOAD`: a human chose the image and sent it. Deliberate, and the strongest
 *   claim anyone can make about what the app should look like.
 * - `FAVICON`: the platform fetched it from the app's own homepage. A good guess,
 *   never a stated preference.
 *
 * The distinction is load-bearing rather than informational: a favicon refetch
 * that runs on its own (a redeploy hook, a backfill) must refuse to overwrite an
 * `UPLOAD`, so the writer has to be able to tell the two apart. An app with no
 * stored icon carries `null` here and renders the generated monogram, which is
 * why every app is distinguishable on day one with nothing uploaded.
 */
// Sourced from the Prisma-generated enum values so the API contract cannot drift
// from the DB enum — same pattern as `VibeAppVisibilitySchema`.
export const VibeAppIconSourceSchema = z.enum(DbEnum.VIBE_APP_ICON_SOURCE_VALUES);

/** `VibeAppIconSourceSchema` as a reader parses it: a value this build does not list reads as unlisted. */
export const VibeAppIconSourceReadSchema = enumReadSchema(DbEnum.VIBE_APP_ICON_SOURCE_VALUES);
export type VibeAppIconSource = z.infer<typeof VibeAppIconSourceSchema>;

/**
 * The resolved icon as a reader sees it: where to fetch the image, and where the
 * image came from.
 *
 * **One nullable object, not two nullable fields.** The database pairs
 * `iconObjectKey` and `iconSource` with a CHECK constraint
 * (`(iconObjectKey IS NULL) = (iconSource IS NULL)`) because a stored image whose
 * provenance is unknown is indistinguishable from one nobody cleared. Grouping
 * them here makes that same half-set state unrepresentable in the contract
 * instead of merely unlikely — a consumer cannot read a `url` without also
 * having been handed its `source`. Precedent: `VibeAppScreenshot` in
 * `VibeAppThumbnail.tsx`, which groups its three fields for the same reason.
 *
 * `url` is resolved server-side from the stored object key. The key itself never
 * crosses the API boundary: it is a bucket-layout detail, and a client that
 * learned it would be one refactor away from composing its own URLs.
 */
export const VibeAppIconSchema = z.object({
  url: z.string().url(),
  source: VibeAppIconSourceReadSchema
});
export type VibeAppIcon = z.infer<typeof VibeAppIconSchema>;

/**
 * Hard ceiling on a decoded icon, in bytes.
 *
 * Sized for what an icon IS rather than for what the transport could bear: a
 * 512×512 PNG with an alpha channel lands comfortably under this, and anything
 * above it is a photograph someone dropped in by mistake. The request body is
 * base64, so the wire cost is ~4/3 of this — well inside the backend's 10mb
 * default JSON limit, which is the ceiling that actually matters for a data-URL
 * upload.
 *
 * Enforced on the DECODED bytes, server-side, before the image is decoded at
 * all. A client-side check is a courtesy to the user, never the guard.
 */
export const VIBE_APP_ICON_MAX_BYTES = 512 * 1024;

/**
 * The square edge, in pixels, every stored icon is normalised to.
 *
 * The console renders the chip at 28–32px and the largest placement in view is
 * the identity bar, so 128 is 4× the biggest consumer — enough headroom for a
 * retina display and for the icon to grow into a card without a re-upload, and
 * small enough that a stored icon is a handful of kilobytes.
 */
export const VIBE_APP_ICON_EDGE_PX = 128;

/**
 * Image types accepted on upload, keyed by the MIME type a `data:` URL declares.
 *
 * The declared type is a ROUTING hint, never a trust boundary — the bytes are
 * decoded by an image library that fails on anything that is not really an
 * image, so a `data:image/png` carrying HTML is rejected on decode rather than
 * on its label. This list exists to reject formats the platform will not store
 * (`image/svg+xml` above all: SVG is a script-bearing document, and serving one
 * from the platform bucket is stored XSS), not to authenticate the payload.
 */
export const VIBE_APP_ICON_ACCEPTED_MIME_TYPES = [
  "image/png",
  "image/jpeg",
  "image/webp",
  "image/gif",
  "image/x-icon",
  "image/vnd.microsoft.icon"
] as const;

/**
 * A `data:<mime>;base64,<payload>` URL carrying the icon.
 *
 * Chosen over a multipart upload because the generated API client has no
 * FormData path: `packages/api-codegen` emits JSON bodies only, and
 * `apps/frontend/src/common/api/generated/vibe.ts` is auto-generated and
 * stamped DO-NOT-EDIT. A data URL keeps the whole endpoint inside the typed
 * contract — validated by this schema on the way in and by the generated hook
 * on the way out — instead of a hand-rolled request beside the client.
 *
 * The regex pins the shape only. Size and decodability are checked on the
 * decoded bytes server-side, where they can actually be enforced.
 */
export const VibeAppIconDataUrlSchema = z
  .string()
  .regex(
    /^data:image\/[a-z0-9.+-]+;base64,[A-Za-z0-9+/]+=*$/,
    "must be a base64 data URL carrying an image"
  )
  // A generous character ceiling so a wildly oversized payload is rejected by
  // the parser rather than base64-decoded first. 4/3 for base64 expansion, plus
  // room for the header and padding; the exact byte check happens after decode.
  .max(Math.ceil((VIBE_APP_ICON_MAX_BYTES * 4) / 3) + 128, "icon is too large");
